import { openDB, type IDBPDatabase } from 'idb';

import type { CatalogoOficinaExterna } from '../../api/oficinaExternaApi';

/**
 * Armazenamento local das telas do INSPETOR (at-114, ADR 0059).
 *
 * 🔴 IndexedDB, e não `localStorage`: o `localStorage` tem ~5 MB, é síncrono e **não guarda
 * Blob** — não serviria para as fotos da visita, que são o item mais pesado da fila.
 *
 * 🔴 Toda leitura e escrita é embrulhada em `try/catch` E avisa quem chamou. IndexedDB falha em
 * janela privada, com armazenamento cheio ou bloqueado por política do navegador, e aqui o
 * silêncio custa uma visita: o inspetor acharia que gravou, sairia da oficina, e o registro
 * nunca teria existido.
 */

const NOME_DO_BANCO = 'soac-oficina-externa';
const VERSAO = 1;

export const STORE_CATALOGO = 'catalogo';
export const STORE_FILA = 'fila';
export const STORE_FOTOS = 'fotos';
export const STORE_RASCUNHO = 'rascunho';
export const STORE_ATENDIMENTOS_LOCAIS = 'atendimentosLocais';

/** `VIDEO` entrou na at-132: o arquivo fica no mesmo store das fotos, com o `mime` dizendo o que é. */
export type TipoDaFila = 'CADASTRO' | 'VISITA' | 'FOTO' | 'VIDEO' | 'SAIDA';
export type EstadoDaFila = 'PENDENTE' | 'ENVIANDO' | 'ENVIADO' | 'RECUSADO';

export interface ItemDaFila {
  clienteId: string;
  tipo: TipoDaFila;
  /** Id do servidor OU `clienteId` do CADASTRO ainda não enviado. */
  atendimentoRef: string;
  /** Só nas fotos e vídeos: o `clienteId` da visita a que eles pertencem. */
  visitaRef?: string;
  /** Placa, só para a tela da fila poder dizer de que veículo o item fala. */
  placa?: string;
  payload: unknown;
  estado: EstadoDaFila;
  tentativas: number;
  erro?: {
    status?: number;
    code?: string;
    mensagem: string;
    /**
     * No `409 PLACA_JA_EM_ATENDIMENTO_EXTERNO`: o id do atendimento que JÁ existe no servidor.
     *
     * 🔴 É o que torna possível "anexar as visitas ao cadastro do colega". Sem guardá-lo aqui, o
     * botão da tela da fila não teria para onde apontar, e a única saída para o inspetor seria
     * descartar visitas que ele realmente fez.
     */
    atendimentoExistenteId?: string;
  };
  criadoEm: string;
  enviadoEm?: string;
  idServidor?: string;
  /** Próxima tentativa (backoff). Itens com `proximaTentativaEm` no futuro são pulados. */
  proximaTentativaEm?: string;
}

export interface FotoLocal {
  clienteId: string;
  visitaRef: string;
  blob: Blob;
  mime: string;
  nome: string;
  criadoEm: string;
}

export interface CatalogoLocal {
  id: 'atual';
  baixadoEm: string;
  dados: CatalogoOficinaExterna;
}

/** Cadastro feito offline — aparece na árvore antes de existir no servidor. */
export interface AtendimentoLocal {
  clienteId: string;
  placa: string;
  oficinaId: string;
  eventNumber: number;
  classe: string;
  modelo?: string;
  entradaEm: string;
  criadoEm: string;
  /** Preenchido quando o cadastro sobe — daí em diante a placa vira o registro do servidor. */
  idServidor?: string;
}

export class ArmazenamentoLocalIndisponivel extends Error {
  constructor(causa?: unknown) {
    super(
      'Não foi possível usar o armazenamento deste aparelho. Não feche a tela antes de a rede ' +
        'voltar: o que você preencher agora pode não ser guardado.',
    );
    this.name = 'ArmazenamentoLocalIndisponivel';
    this.cause = causa;
  }
}

let promessaDoBanco: Promise<IDBPDatabase> | null = null;

const abrir = (): Promise<IDBPDatabase> => {
  if (!promessaDoBanco) {
    promessaDoBanco = openDB(NOME_DO_BANCO, VERSAO, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_CATALOGO)) {
          db.createObjectStore(STORE_CATALOGO, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORE_FILA)) {
          const fila = db.createObjectStore(STORE_FILA, { keyPath: 'clienteId' });
          fila.createIndex('estado', 'estado');
          fila.createIndex('criadoEm', 'criadoEm');
        }
        if (!db.objectStoreNames.contains(STORE_FOTOS)) {
          db.createObjectStore(STORE_FOTOS, { keyPath: 'clienteId' });
        }
        if (!db.objectStoreNames.contains(STORE_RASCUNHO)) {
          db.createObjectStore(STORE_RASCUNHO, { keyPath: 'atendimentoRef' });
        }
        if (!db.objectStoreNames.contains(STORE_ATENDIMENTOS_LOCAIS)) {
          db.createObjectStore(STORE_ATENDIMENTOS_LOCAIS, { keyPath: 'clienteId' });
        }
      },
    }).catch((erro) => {
      // Zera a promessa para que uma tentativa futura (outra aba, outro momento) possa
      // reabrir — um `catch` que memoizasse a falha condenaria a sessão inteira.
      promessaDoBanco = null;
      throw new ArmazenamentoLocalIndisponivel(erro);
    });
  }
  return promessaDoBanco;
};

// ------------------------------------------------------------------ catálogo

export const salvarCatalogo = async (dados: CatalogoOficinaExterna): Promise<CatalogoLocal> => {
  const registro: CatalogoLocal = { id: 'atual', baixadoEm: new Date().toISOString(), dados };
  const db = await abrir();
  await db.put(STORE_CATALOGO, registro);
  return registro;
};

export const lerCatalogo = async (): Promise<CatalogoLocal | null> => {
  const db = await abrir();
  return (await db.get(STORE_CATALOGO, 'atual')) ?? null;
};

// ---------------------------------------------------------------------- fila

export const enfileirar = async (item: ItemDaFila): Promise<void> => {
  const db = await abrir();
  await db.put(STORE_FILA, item);
};

export const lerFila = async (): Promise<ItemDaFila[]> => {
  const db = await abrir();
  const itens: ItemDaFila[] = await db.getAll(STORE_FILA);
  return itens.sort((a, b) => a.criadoEm.localeCompare(b.criadoEm));
};

export const atualizarItemDaFila = async (
  clienteId: string,
  mudancas: Partial<ItemDaFila>,
): Promise<void> => {
  const db = await abrir();
  const atual: ItemDaFila | undefined = await db.get(STORE_FILA, clienteId);
  if (!atual) return;
  await db.put(STORE_FILA, { ...atual, ...mudancas });
};

export const removerItemDaFila = async (clienteId: string): Promise<void> => {
  const db = await abrir();
  await db.delete(STORE_FILA, clienteId);
  await db.delete(STORE_FOTOS, clienteId).catch(() => undefined);
};

/**
 * Varre os itens ENVIADOS com mais de 7 dias.
 *
 * A fila é histórico CURTO, não arquivo: o que já subiu vive no servidor, e guardar Blob de foto
 * no aparelho indefinidamente encheria o armazenamento do celular — que é o recurso que faz a
 * próxima visita conseguir ser gravada.
 */
export const limparEnviadosAntigos = async (dias = 7): Promise<void> => {
  const limite = Date.now() - dias * 86_400_000;
  const db = await abrir();
  const itens: ItemDaFila[] = await db.getAll(STORE_FILA);

  for (const item of itens) {
    if (item.estado !== 'ENVIADO') continue;
    const quando = Date.parse(item.enviadoEm ?? item.criadoEm);
    if (Number.isFinite(quando) && quando < limite) {
      await db.delete(STORE_FILA, item.clienteId);
      await db.delete(STORE_FOTOS, item.clienteId).catch(() => undefined);
    }
  }
};

// --------------------------------------------------------------------- fotos

export const guardarFoto = async (foto: FotoLocal): Promise<void> => {
  const db = await abrir();
  await db.put(STORE_FOTOS, foto);
};

export const lerFoto = async (clienteId: string): Promise<FotoLocal | null> => {
  const db = await abrir();
  return (await db.get(STORE_FOTOS, clienteId)) ?? null;
};

// ------------------------------------------------------------------ rascunho

export const salvarRascunho = async (atendimentoRef: string, dados: unknown): Promise<void> => {
  const db = await abrir();
  await db.put(STORE_RASCUNHO, { atendimentoRef, dados, salvoEm: new Date().toISOString() });
};

export const lerRascunho = async (atendimentoRef: string): Promise<unknown | null> => {
  const db = await abrir();
  const registro = await db.get(STORE_RASCUNHO, atendimentoRef);
  return registro?.dados ?? null;
};

export const descartarRascunho = async (atendimentoRef: string): Promise<void> => {
  const db = await abrir();
  await db.delete(STORE_RASCUNHO, atendimentoRef);
};

// --------------------------------------------------- atendimentos locais

export const salvarAtendimentoLocal = async (registro: AtendimentoLocal): Promise<void> => {
  const db = await abrir();
  await db.put(STORE_ATENDIMENTOS_LOCAIS, registro);
};

export const lerAtendimentosLocais = async (): Promise<AtendimentoLocal[]> => {
  const db = await abrir();
  return db.getAll(STORE_ATENDIMENTOS_LOCAIS);
};

export const removerAtendimentoLocal = async (clienteId: string): Promise<void> => {
  const db = await abrir();
  await db.delete(STORE_ATENDIMENTOS_LOCAIS, clienteId);
};

/** UUID do aparelho — a chave de idempotência que o backend usa (ADR 0059). */
export const novoClienteId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback para navegador antigo e para o jsdom dos testes: o formato precisa passar pelo
  // `@IsUUID` do backend, então é v4 montado à mão, nunca um `Date.now()`.
  const hex = (n: number) =>
    Array.from({ length: n }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${hex(8)}-${hex(4)}-4${hex(3)}-8${hex(3)}-${hex(12)}`;
};
